import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Heart, Star, Calendar, Clock, Tag, Edit3, Trash2 } from "lucide-react";
import { Movie, TVShow, WatchlistItem, Language } from "../../types";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { tmdbApi } from "../../services/tmdb";
import { useQuery } from "@tanstack/react-query";
import { favoritesService } from "../../services/favoritesService";
import { useTranslation } from "react-i18next";


const Card = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  background: var(--color-background-card);
  box-shadow: var(--shadow-md);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
  }
`;

const PosterImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--border-radius-lg);
  transition: transform 0.3s ease;
`;

const Content = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--spacing-4);
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent);
  border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
  color: var(--color-text-primary);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const PostLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;

  &:hover ~ ${Content} {
    opacity: 1;
  }
`;

const Title = styled.h2`
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  color: var(--color-text-primary);
  font-weight: 600;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const Overlay = styled.div<{ $showContent: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  opacity: ${({ $showContent }) => ($showContent ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const CardContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  
  &:hover ${Overlay} {
    opacity: 1;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  gap: var(--spacing-3);
  margin-bottom: var(--spacing-2);
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: var(--spacing-2);
    margin-bottom: var(--spacing-1);
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ButtonContainer = styled.div`
  position: absolute;
  top: var(--spacing-2);
  right: var(--spacing-2);
  display: flex;
  gap: var(--spacing-2);
  z-index: 5;
  
  @media (max-width: 768px) {
    top: var(--spacing-1);
    right: var(--spacing-1);
    gap: var(--spacing-1);
  }
`;

const IconButton = styled.button<{ variant?: 'delete' | 'active' }>`
  background: rgba(0, 0, 0, 0.7);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => {
    if (props.variant === 'delete') return '#FFD700';
    if (props.variant === 'active') return '#FFD700';
    return 'var(--color-text-primary)';
  }};
  opacity: 0;

  ${Card}:hover & {
    opacity: 1;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    opacity: 1;
    background: rgba(0, 0, 0, 0.8);
  }

  &:hover {
    background: ${props => {
      if (props.variant === 'delete') return '#FFD700';
      if (props.variant === 'active') return '#FFD700';
      return 'rgba(255, 255, 255, 0.1)';
    }};
    color: #000;
    transform: scale(1.1);
  }
`;

const AdminButton = styled(IconButton)`
  background: var(--color-primary);
  color: white;
  
  &:hover {
    background: var(--color-primary-dark);
  }
  
  @media (max-width: 768px) {
    opacity: 1;
  }
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  opacity: 0;
  transition: all 0.3s ease;

  &:hover {
    background: var(--color-primary);
    transform: translate(-50%, -50%) scale(1.1);
  }

  ${Card}:hover & {
    opacity: 1;
  }
`;

const InfoText = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

interface MediaCardProps {
  item: Movie | TVShow;
  type: 'movie' | 'tv';
  onToggleWatchlist?: () => void;
  isInWatchlist?: boolean;
  showRemoveButton?: boolean;
  onRemove?: () => void;
  className?: string;
}

const getYear = (item: Movie | TVShow, type: 'movie' | 'tv'): number | string => {
  const date = type === 'movie' 
    ? (item as Movie).release_date 
    : (item as TVShow).first_air_date;
  
  if (!date) return 'Bientôt';
  
  const year = new Date(date).getFullYear();
  return isNaN(year) ? 'Bientôt' : year;
};

const getDuration = (item: Movie | TVShow, type: 'movie' | 'tv', t: any): string | null => {
  if (type === 'movie' && 'runtime' in item) {
    return item.runtime ? `${item.runtime}min` : null;
  }
  if (type === 'tv' && 'number_of_seasons' in item) {
    return item.number_of_seasons ? `${item.number_of_seasons} ${t('seasons')}` : null;
  }
  return null;
};

export const MediaCard: React.FC<MediaCardProps> = ({
  item,
  type,
  onToggleWatchlist,
  isInWatchlist,
  showRemoveButton,
  onRemove,
  className,
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [translatedTitle, setTranslatedTitle] = useState('');

  const title = 'title' in item ? item.title : item.name;
  
  // Vérifier si le film/série a une date de sortie valide
  const releaseDate = type === 'movie' 
    ? (item as Movie).release_date 
    : (item as TVShow).first_air_date;
  
  const isComingSoon = !releaseDate || releaseDate === '' || new Date(releaseDate).getFullYear() > new Date().getFullYear() + 2;
  
  const linkPath = isComingSoon 
    ? '/coming-soon'
    : `/${type}/${item.id}`;
  
  const linkState = isComingSoon 
    ? { title: title, type: type }
    : undefined;

  useEffect(() => {
    const translateContent = async () => {
      try {
        const targetLang = i18n.language as Language;
        // (traduction désactivée)
        setTranslatedTitle(title);
      } catch (error) {
        console.error('Translation error:', error);
        setTranslatedTitle(title);
      }
    };

    translateContent();
  }, [title, i18n.language]);

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Vérifier si le film/série a une date de sortie
    const releaseDate = type === 'movie' 
      ? (item as Movie).release_date 
      : (item as TVShow).first_air_date;
    
    // Si pas de date ou date invalide, rediriger vers la page "Bientôt"
    if (!releaseDate || releaseDate === '' || new Date(releaseDate).getFullYear() > new Date().getFullYear() + 2) {
      navigate('/coming-soon', { 
        state: { 
          title: title,
          type: type 
        } 
      });
      return;
    }
    
    navigate(`/${type}/${item.id}`);
  };

  return (
    <Card className={className}>
      <CardContent>
        <PosterImage
          src={`https://image.tmdb.org/t/p/w500${item.poster_path}`}
          alt={translatedTitle}
          loading="lazy"
        />
        <PlayButton onClick={handlePlayClick}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </PlayButton>
        <Overlay $showContent={true}>
          <Title>{translatedTitle}</Title>
          <InfoText>
            {item.vote_average !== undefined && item.vote_average > 0 && (
              <MetaItem>
                <Star size={16} />
                {item.vote_average.toFixed(1)}
              </MetaItem>
            )}
            <MetaItem>
              <Calendar size={16} />
              {getYear(item, type)}
            </MetaItem>
            {getDuration(item, type, i18n.t) && (
              <MetaItem>
                <Clock size={16} />
                {getDuration(item, type, i18n.t)}
              </MetaItem>
            )}
          </InfoText>
        </Overlay>
        <ButtonContainer>
          {onToggleWatchlist && (
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleWatchlist();
              }}
              aria-label={isInWatchlist ? 'Retirer de la watchlist' : 'Ajouter à la watchlist'}
              title={isInWatchlist ? 'Retirer de la watchlist' : 'Ajouter à la watchlist'}
              variant={isInWatchlist ? 'active' : undefined}
            >
              <Heart size={16} fill={isInWatchlist ? 'currentColor' : 'none'} />
            </IconButton>
          )}
          {showRemoveButton && onRemove && (
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove();
              }}
              variant="delete"
              aria-label="Retirer"
              title="Retirer"
            >
              <Trash2 size={16} />
            </IconButton>
          )}
        </ButtonContainer>
      </CardContent>
      <PostLink to={linkPath} state={linkState} />
    </Card>
  );
};

export default MediaCard;
